// Page-side WalletConnect (wallet-role) hook for the in-app dApp browser.
// Pairs with the worker bridge (walletConnectWalletBridge.ts): dispatches WC_PAIR
// / WC_DISCONNECT and tracks live sessions from the relayed 'walletConnectEvent'.
// Lets the webapp wallet connect to ANY external dApp via a pasted `wc:` URI — no
// dApp changes, no extension.
import { useCallback, useEffect, useState } from 'react'

import eventBus from '@common/services/event/eventBus'
import useBackgroundService from '@web/hooks/useBackgroundService'

export type WcConnection = { topic: string; name: string; url: string; icon: string | null }
export type WcStatus = 'idle' | 'pairing' | 'connected' | 'error'

// The in-app-browser catalog dApp being embedded when the user pairs. Passed to
// the worker so the settled WC session folds onto THIS dApp (its catalog id/url)
// instead of the dApp's self-reported metadata.url — which is frequently a
// different host (e.g. PulseX embeds ipfs.app.pulsex.com but reports
// pulsex.mypinata.cloud), causing a duplicate "Connected" entry and a header
// stuck on "Connect dApp". See walletConnectWalletBridge.ts.
export type WcEmbedDapp = { id: string; url: string; name?: string; icon?: string | null }

// A pending WC connection request awaiting the user's approve/reject decision.
// Rendered by WalletConnectProposalOverlay; the worker holds the SDK proposal
// until we answer (WC_APPROVE_PROPOSAL / WC_REJECT_PROPOSAL).
export type WcProposal = {
  id: number
  name: string
  url: string
  icon: string | null
  chains: string[]
}

const useWalletConnect = () => {
  const { dispatch } = useBackgroundService()
  const [connections, setConnections] = useState<WcConnection[]>([])
  // True once the worker has replied with its live-session view at least once
  // (a `sessions` snapshot or any connect/disconnect event). Consumers that
  // reconcile a persisted "Connected" flag against `connections` must wait for
  // this — before the first reply `connections` is an empty array that would
  // otherwise read as "no live session" and trigger a false disconnect.
  const [sessionsLoaded, setSessionsLoaded] = useState(false)
  const [status, setStatus] = useState<WcStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [proposal, setProposal] = useState<WcProposal | null>(null)

  useEffect(() => {
    const onEvent = ({ event, data }: { event: string; data: any }) => {
      // Any of these means the worker has answered with its session view.
      if (event === 'sessions' || event === 'connected' || event === 'disconnected')
        setSessionsLoaded(true)
      if (event === 'proposal') {
        // SECURITY: surface the proposal for explicit user consent — pasting the
        // wc: URI only starts pairing, it is not blanket approval. The actual
        // approve/reject is dispatched from approveProposal/rejectProposal below.
        setProposal({
          id: data?.id,
          name: data?.name || data?.url || 'dApp',
          url: data?.url || '',
          icon: data?.icon || null,
          chains: Array.isArray(data?.chains) ? data.chains : []
        })
        setStatus('pairing')
      } else if (event === 'connected') {
        setProposal(null)
        setConnections((prev) => [...prev.filter((c) => c.topic !== data.topic), data])
        setStatus('connected')
        setError(null)
      } else if (event === 'disconnected') {
        setConnections((prev) => prev.filter((c) => c.topic !== data.topic))
      } else if (event === 'sessions') {
        // Full snapshot of live sessions (on mount / after reload).
        setConnections(Array.isArray(data?.sessions) ? data.sessions : [])
      } else if (event === 'error') {
        setProposal(null)
        setStatus('error')
        setError(data?.message || 'Connection failed')
      }
    }
    eventBus.addEventListener('walletConnectEvent', onEvent)
    // Ask the worker for any sessions that survived a reload so the list is
    // accurate even if this hook mounts after a connection already exists.
    dispatch({ type: 'WC_GET_SESSIONS' } as any)

    // Fallback poll: the WalletConnect SDK only emits `session_delete` on OUR
    // side for a dApp-initiated disconnect after its ack publish round-trips
    // through the relay (`relayer.once('publish', ...)` inside the SDK's own
    // `onSessionDeleteRequest`). Confirmed live: if the relay connection blips
    // around that moment (observed "Relayer disconnected" in the SDK's own
    // logs during a real dApp-side disconnect), that publish never fires, the
    // event never reaches us, and `connections` goes stale even though the
    // session is actually gone — the pill only recovers on a full remount
    // (which re-dispatches WC_GET_SESSIONS fresh). Poll periodically so it
    // self-heals within a few seconds instead of requiring that remount.
    const poll = setInterval(() => dispatch({ type: 'WC_GET_SESSIONS' } as any), 5000)

    return () => {
      eventBus.removeEventListener('walletConnectEvent', onEvent)
      clearInterval(poll)
    }
  }, [dispatch])

  const pair = useCallback(
    (uri: string, embedDapp?: WcEmbedDapp) => {
      setError(null)
      setStatus('pairing')
      dispatch({ type: 'WC_PAIR', params: { uri, embedDapp } } as any)
    },
    [dispatch]
  )

  const disconnect = useCallback(
    (topic: string) => dispatch({ type: 'WC_DISCONNECT', params: { topic } } as any),
    [dispatch]
  )

  // Disconnect by dApp origin instead of a topic from our own (possibly not-yet-
  // populated) `connections` list. `WC_GET_SESSIONS` is fire-and-forget on mount,
  // so a caller that checks `connections` right after mounting can race an empty
  // array and find nothing to disconnect even though a live session exists. The
  // worker resolves the origin against its own always-current session list
  // instead, so this works regardless of whether `connections` has loaded yet.
  const disconnectByOrigin = useCallback(
    (origin: string) => dispatch({ type: 'WC_DISCONNECT', params: { origin } } as any),
    [dispatch]
  )

  const approveProposal = useCallback(() => {
    if (!proposal) return
    dispatch({ type: 'WC_APPROVE_PROPOSAL', params: { id: proposal.id } } as any)
    setProposal(null)
    setStatus('pairing')
  }, [dispatch, proposal])

  const rejectProposal = useCallback(() => {
    if (!proposal) return
    dispatch({ type: 'WC_REJECT_PROPOSAL', params: { id: proposal.id } } as any)
    setProposal(null)
    setStatus('idle')
  }, [dispatch, proposal])

  return {
    connections,
    sessionsLoaded,
    status,
    error,
    proposal,
    pair,
    disconnect,
    disconnectByOrigin,
    approveProposal,
    rejectProposal
  }
}

export default useWalletConnect
